import React from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  ArrowLeft,
  ShieldCheck,
} from "lucide-react";

/**
 * Iter 708 — pre-publish review checklist (website post form).
 *
 * Renders the POST /classifieds/listings/precheck response as a modal
 * "Review" step between the form and Publish:
 *  • staff (role === "staff"): every required_confirm row gets a
 *    "Confirmed" checkbox — Publish stays disabled until all are ticked;
 *    optional reviewer note is sent as staff_review.note. The ad is then
 *    auto-approved (no Pending-review queue).
 *  • sellers: read-only rows as helpful feedback; a "fail" disables
 *    Publish and points at the failing row's hint. Warns never block.
 * Fix affordances: category suggestion re-selects the category,
 * duplicate matches link to the existing ads, price median renders a
 * "Similar ads around $X" line.
 */

const STATUS_ICON = {
  pass: <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />,
  warn: <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />,
  fail: <XCircle className="w-5 h-5 text-red-500 shrink-0" />,
};

const fmtNzd = (n) => `$${new Intl.NumberFormat("en-NZ").format(Math.round(n))}`;

export default function ReviewChecklist({
  data,
  confirmed,
  onToggleConfirm,
  note,
  onNote,
  onPublish,
  publishing,
  onBack,
  onSwitchCategory,
}) {
  if (!data) return null;
  const isStaff = data.role === "staff";
  const required = data.required_confirm || [];
  const allConfirmed = required.every((k) => confirmed.includes(k));
  const sellerBlocked = !isStaff && data.can_publish === false;
  const canPublish = isStaff ? allConfirmed : !sellerBlocked;
  const summary = data.summary || {};

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 p-0 sm:p-4"
      data-testid="review-checklist-modal"
    >
      <div className="w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-soft px-5 py-4 flex items-center justify-between gap-3 z-10">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">
              Review your listing
            </h2>
            <p className="text-xs text-slate-500" data-testid="review-summary">
              {summary.pass ?? 0} passed · {summary.warn ?? 0} to review
              {summary.fail ? ` · ${summary.fail} blocking` : ""}
            </p>
          </div>
          {isStaff && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] text-[11px] font-black px-2.5 py-1"
              data-testid="review-staff-badge"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Staff review
            </span>
          )}
        </div>

        {/* Check rows */}
        <ul className="divide-y divide-subtle px-5">
          {(data.checks || []).map((c) => (
            <CheckRow
              key={c.key}
              check={c}
              isStaff={isStaff}
              confirmed={confirmed.includes(c.key)}
              onToggleConfirm={() => onToggleConfirm(c.key)}
              onSwitchCategory={onSwitchCategory}
            />
          ))}
        </ul>

        {/* Staff reviewer note */}
        {isStaff && (
          <div className="px-5 pt-4">
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">
              Reviewer note (optional)
            </label>
            <textarea
              data-testid="review-note-input"
              value={note}
              onChange={(e) => onNote(e.target.value.slice(0, 500))}
              rows={2}
              placeholder="Anything worth recording about this approval…"
              className="input"
            />
          </div>
        )}

        {/* Seller blocked explainer */}
        {sellerBlocked && (
          <p
            className="mx-5 mt-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3"
            data-testid="review-blocked-note"
          >
            Fix the items marked ⛔ above, then try again.
          </p>
        )}

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-white border-t border-soft px-5 py-4 flex items-center justify-between gap-3 mt-4">
          <button
            type="button"
            data-testid="review-back-btn"
            onClick={onBack}
            disabled={publishing}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to edit
          </button>
          <button
            type="button"
            data-testid="review-publish-btn"
            onClick={onPublish}
            disabled={publishing || !canPublish}
            className={`inline-flex items-center gap-2 rounded px-6 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold shadow-card transition-colors ${
              publishing || !canPublish ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            {publishing && <Loader2 className="w-4 h-4 animate-spin" />}
            {isStaff ? "Confirm & Publish" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CheckRow({ check: c, isStaff, confirmed, onToggleConfirm, onSwitchCategory }) {
  const needsConfirm = isStaff && (c.confirm_required || false);
  return (
    <li className="py-3.5 flex items-start gap-3" data-testid={`review-check-row-${c.key}`}>
      {STATUS_ICON[c.status] || STATUS_ICON.warn}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-slate-900">{c.label}</div>
        {c.detail && <div className="text-sm text-slate-700 mt-0.5">{c.detail}</div>}
        {c.hint && <div className="text-xs text-slate-400 mt-0.5">{c.hint}</div>}

        {/* Fix affordances (WO 708 §4) */}
        {c.key === "price" && c.median != null && c.status !== "pass" && (
          <div className="text-xs text-slate-500 mt-1" data-testid="review-price-median">
            Similar ads around {fmtNzd(c.median)}
          </div>
        )}
        {c.key === "category" && c.suggested_slug && (
          <button
            type="button"
            data-testid="review-switch-category"
            onClick={() => onSwitchCategory(c.suggested_slug, c.suggested_name)}
            className="mt-1.5 inline-flex items-center rounded-full border border-[color:var(--brand-blue)] text-[color:var(--brand-blue)] text-xs font-bold px-3 py-1 hover:bg-[color:var(--brand-blue-wash)] transition-colors"
          >
            Switch to {c.suggested_name || c.suggested_slug}
          </button>
        )}
        {c.key === "duplicate" && Array.isArray(c.matches) && c.matches.length > 0 && (
          <ul className="mt-1.5 space-y-1">
            {c.matches.slice(0, 3).map((m) => (
              <li key={m.id}>
                <Link
                  to={`/ad/${m.id}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`review-duplicate-link-${m.id}`}
                  className="text-xs font-semibold text-[color:var(--brand-blue)] hover:underline"
                >
                  {m.title}
                  {m.similarity != null ? ` · ${Math.round(m.similarity * 100)}% similar` : ""}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      {needsConfirm && (
        <label className="flex items-center gap-1.5 text-xs font-bold text-slate-600 cursor-pointer select-none shrink-0 pt-0.5">
          <input
            type="checkbox"
            data-testid={`review-check-confirm-${c.key}`}
            checked={confirmed}
            onChange={onToggleConfirm}
            className="w-4 h-4 accent-[color:var(--brand-blue)]"
          />
          Confirmed
        </label>
      )}
    </li>
  );
}
